import { type RefObject, useEffect, useState } from 'react';

/**
 * Reports whether an element came near the viewport. It answers `true` from
 * the first `IntersectionObserver` hit on and stops observing then.
 *
 * A component uses it to defer a billed or heavy load, such as the Google Maps
 * script, until the shopper can see the result. An environment without
 * `IntersectionObserver`, such as jsdom, answers `false` for good.
 */
export const useNearViewport = (ref: RefObject<Element | null>, rootMargin: string) => {
  const [near, setNear] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          setNear(true);
        }
      },
      { rootMargin }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return near;
};
